import React from 'react'
import {
    // clientThemeBlue,
    clientThemeOutpayce,
    clientThemeKraken,
    clientThemeMonavate,
    clientThemeSabre,
    clientThemeLastminute,
    clientThemeRed,
    clientThemeLVMH,
    clientThemePaymentsave,
    // clientThemePaysafe,
    clientThemeSofi,
    // clientThemeChoiceHotels,
    clientThemeUDMS,
    clientThemeBarclays,
    clientThemeShell
} from 'theme-exports'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { useUiState } from 'hooks/cb-api/useUiState'
import { Theme } from '@mui/material'
import { createMuiThemeFromClientTheme } from 'theme'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import {
    getSubdomain,
    getThemeVariantForSubdomain,
} from 'context/AuthedUser/utils/domainUtils'

// Extended client theme interface to include fontFamilyConfig
interface FontFamilyConfig {
    primary: string
    fallback: string
}

interface ClientThemeTypography {
    fontColor: {
        primaryText: string
        secondaryText: string
    }
    fontFamily: string[]
    fontFamilyConfig?: FontFamilyConfig
    h1: { fontSize: string }
    h2: { fontSize: string }
    h3: { fontSize: string }
    h4: { fontSize: string }
    h5: { fontSize: string }
    h6: { fontSize: string }
    subtitle1: { fontSize: string }
    subtitle2: { fontSize: string }
    body1: { fontSize: string }
    body2: { fontSize: string }
    button1: { fontSize: string }
    caption: { fontSize: string }
    overline: { fontSize: string }
    [key: string]: any // For other properties
}

interface ClientTheme {
    typography: ClientThemeTypography
    [key: string]: any // For other properties
}

// Define the type for theme variant objects
export interface ThemeVariant {
    id: number
    logo: string
    logoIcon?: string
    childLogo?: string
    theme: ClientTheme
    mockDataFolder: string
}

export interface ThemingResult {
    clientTheme: ClientTheme
    muiTheme: Theme
    logoPath: string
    logoIconPath?: string
    childLogoPath?: string
    themeVariantObj: ThemeVariant
}

export const CLIENT_THEME_VARIANT: Record<string, ThemeVariant> = {
    DISPUTE_PRO: {
        id: 1,
        logo: 'brand-logo-lg-udms.png',
        logoIcon: 'brand-logo-lg-udms-icon.png',
        theme: clientThemeUDMS,
        mockDataFolder: 'cb911',
    },
    // SUB ISSUER OF AMADEUS
    OUTPAYCE: {
        id: 2,
        logo: 'brand-logo-lg_amadeus_outpayce.png',
        theme: clientThemeOutpayce,
        mockDataFolder: 'outpayce',
    },
    // SUB ISSUER OF SABRE
    LASTMINUTE: {
        id: 3,
        logo: 'brand-logo-lg_sabre.png',
        childLogo: 'brand-logo-lg_sabre_lastminute_white.png',
        theme: clientThemeLastminute,
        mockDataFolder: 'lastminute',
    },
    // SUB ISSUER OF MONAVATE
    KRAKEN: {
        id: 4,
        logo: 'brand-logo-lg_kraken.png',
        theme: clientThemeKraken,
        mockDataFolder: 'kraken',
    },
    MONAVATE: {
        id: 5,
        logo: 'brand-logo-lg_monavate_yellow.png',
        theme: clientThemeMonavate,
        mockDataFolder: 'monavate',
    },
    AMADEUS: {
        id: 6,
        logo: 'brand-logo-lg-udms.png',
        logoIcon: 'brand-logo-lg-udms-icon.png',
        theme: clientThemeUDMS,
        mockDataFolder: 'cb911',
    },
    SABRE: {
        id: 7,
        logo: 'brand-logo-lg_sabre.png',
        theme: clientThemeSabre,
        mockDataFolder: 'lastminute',
    },
    LVMH: {
        id: 8,
        logo: 'brand-logo-lg_lvmh.png',
        theme: clientThemeLVMH,
        mockDataFolder: 'lvmh',
    },
    CB911: {
        id: 9,
        logo: 'brand-logo-lg-cb911.png',
        theme: clientThemeRed,
        mockDataFolder: 'cb911',
    },
    PAYMENTSAVE: {
        id: 10,
        logo: 'brand-logo-lg_paymentsave.png',
        theme: clientThemePaymentsave,
        mockDataFolder: 'cb911',
    },
    SOFI: {
        id: 11,
        logo: 'brand-logo-lg_sofi.png',
        theme: clientThemeSofi,
        mockDataFolder: 'sofi',
    },
    CHOICE_HOTELS: {
        id: 13,
        logo: 'brand-logo-lg_shell.png',
        theme: clientThemeShell,
        mockDataFolder: 'choiceHotels',
    },
    BARCLAYS: {
        id: 14,
        logo: 'brand-logo-lg_barclays.png',
        theme: clientThemeBarclays,
        mockDataFolder: 'barclays',
    },
}

export const useThemeStyling = (): ThemingResult => {
    const { user } = useAuthedUser()
    const uiState = useUiState()
    const { enabled: themeVariationEnabled } = useFeatureToggle(
        'HAS_THEME_VARIATIONS'
    )

    // Get the theme variant ID from either source, with fallback logic
    let themeVariantId = user?.theme_variant || uiState?.whoami?.theme_variant

    // For On-Demand Case Builder Issuer theming and Cardholder Feedback:
    if (['/builder', '/feedback'].includes(window.location.pathname)) {
        const subdomain: string = getSubdomain()
        themeVariantId = getThemeVariantForSubdomain(subdomain)
    }

    if (process.env.NODE_ENV === 'development') {
        if (!process.env.REACT_APP_THEME_VARIANT_ID) {
            throw new Error('REACT_APP_THEME_VARIANT_ID is not set')
        }

        themeVariantId = Number(process.env.REACT_APP_THEME_VARIANT_ID)
    }

    // Find the theme variant object based on the variant ID
    const getThemeVariantObject = () => {
        // Find the matching theme variant object
        const variantEntry = Object.entries(CLIENT_THEME_VARIANT).find(
            ([_, variant]) => variant.id === themeVariantId
        )

        // Return the found variant or default to DISPUTE_PRO
        return variantEntry ? variantEntry[1] : CLIENT_THEME_VARIANT.DISPUTE_PRO
    }

    // Get the theme variant object
    const themeVariantObj = getThemeVariantObject()

    // Get the client theme from the theme variant object
    const clientTheme = themeVariantObj.theme

    // Generate the MUI theme directly from the client theme
    const muiTheme = createMuiThemeFromClientTheme(clientTheme)

    // Determine the appropriate logo path based on theme settings
    const publicUrl = process.env.PUBLIC_URL
    let logoPath = publicUrl + '/images/logos/' + themeVariantObj.logo
    const logoIconPath = themeVariantObj.logoIcon
        ? publicUrl + '/images/logos/' + themeVariantObj.logoIcon
        : undefined
    let childLogoPath = themeVariantObj.childLogo
        ? publicUrl + '/images/logos/' + themeVariantObj.childLogo
        : undefined

    // If theme variations are not enabled, use the default logo
    if (!themeVariationEnabled) {
        logoPath =
            publicUrl + '/images/logos/' + CLIENT_THEME_VARIANT.DISPUTE_PRO.logo
    }

    // Apply font family from theme to body element at runtime and load font CSS if needed
    React.useEffect(() => {
        // Use type assertion to access the fontFamilyConfig property
        const typography = clientTheme.typography as any
        const fontConfig = typography.fontFamilyConfig as
            | FontFamilyConfig
            | undefined

        // Load Poppins font CSS file if using Monovate theme (ID: 5)
        if (themeVariantId === CLIENT_THEME_VARIANT.MONAVATE.id) {
            // Create a style element to define the font-face rules
            const styleElement = document.createElement('style')
            styleElement.id = 'poppins-font-styles'
            styleElement.textContent = `
                @font-face {
                    font-family: 'Poppins';
                    src: url('${process.env.PUBLIC_URL}/fonts/poppins/Poppins-Regular.ttf') format('truetype');
                    font-weight: 400;
                    font-style: normal;
                    font-display: swap;
                }
                @font-face {
                    font-family: 'Poppins';
                    src: url('${process.env.PUBLIC_URL}/fonts/poppins/Poppins-Medium.ttf') format('truetype');
                    font-weight: 500;
                    font-style: normal;
                    font-display: swap;
                }
                @font-face {
                    font-family: 'Poppins';
                    src: url('${process.env.PUBLIC_URL}/fonts/poppins/Poppins-Bold.ttf') format('truetype');
                    font-weight: 700;
                    font-style: normal;
                    font-display: swap;
                }
            `

            // Remove any existing Poppins styles to avoid duplicates
            const existingStyles = document.getElementById(
                'poppins-font-styles'
            )
            if (existingStyles) {
                existingStyles.remove()
            }

            // Add the style element to the document head
            document.head.appendChild(styleElement)
        }

        // Load Inter and Robot CSS file if using Sabre theme (ID: 7)
        if (
            themeVariantId === CLIENT_THEME_VARIANT.DISPUTE_PRO.id ||
            themeVariantId === CLIENT_THEME_VARIANT.CB911.id ||
            themeVariantId === CLIENT_THEME_VARIANT.SABRE.id ||
            themeVariantId === CLIENT_THEME_VARIANT.LASTMINUTE.id ||
            themeVariantId === CLIENT_THEME_VARIANT.AMADEUS.id
        ) {
            // Create a style element to define the font-face rules
            const styleElement = document.createElement('style')
            styleElement.id = 'default-font-styles'
            styleElement.textContent = `
                @font-face {
                    font-family: 'Inter';
                    src: url('${process.env.PUBLIC_URL}/fonts/inter/Inter-VariableFont.ttf') format('truetype');
                    font-weight: 100 900;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'Inter';
                    src: url('${process.env.PUBLIC_URL}/fonts/inter/Inter-Italic-VariableFont.ttf') format('truetype');
                    font-weight: 100 900;
                    font-style: italic;
                }

                @font-face {
                    font-family: 'Roboto';
                    src: url('${process.env.PUBLIC_URL}/fonts/roboto/Roboto-VariableFont.ttf') format('truetype');
                    font-weight: 100 900;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'Roboto';
                    src: url('${process.env.PUBLIC_URL}/fonts/roboto/Roboto-Italic-VariableFont.ttf') format('truetype');
                    font-weight: 100 900;
                    font-style: italic;
                }
            `

            // Remove any existing Poppins styles to avoid duplicates
            const existingStyles = document.getElementById(
                'default-font-styles'
            )
            if (existingStyles) {
                existingStyles.remove()
            }

            // Add the style element to the document head
            document.head.appendChild(styleElement)
        }

        if (fontConfig) {
            const { primary, fallback } = fontConfig
            // Apply font family to body element
            const fontFamilyValue = fallback
                ? `${primary}, ${fallback}`
                : primary
            document.body.style.fontFamily = fontFamilyValue
            // Also apply !important to override any CSS rules
            document.body.style.setProperty(
                'font-family',
                fontFamilyValue,
                'important'
            )
        }

        if (clientTheme?.typography) {
            const root = document.documentElement
            root.style.setProperty(
                '--h5-font-size',
                clientTheme.typography.h5.fontSize
            )
            root.style.setProperty(
                '--h5-font-color',
                clientTheme.typography.fontColor.primaryText
            )
        }

        if (clientTheme?.primary) {
            const root = document.documentElement
            root.style.setProperty('--theme-primary', clientTheme.primary)
        }
        if (clientTheme?.secondary) {
            const root = document.documentElement
            root.style.setProperty('--theme-secondary', clientTheme.secondary)
        }
        if (clientTheme?.primaryDark) {
            const root = document.documentElement
            root.style.setProperty(
                '--theme-primary-dark',
                clientTheme.primaryDark
            )
        }
        if (clientTheme?.mainLayout?.backgroundColor) {
            const root = document.documentElement
            root.style.setProperty(
                '--theme-background-color',
                clientTheme.mainLayout.backgroundColor
            )
        }
        if (clientTheme?.primaryLight) {
            const root = document.documentElement
            root.style.setProperty(
                '--theme-primary-light',
                clientTheme.primaryLight
            )
        }

        if (clientTheme?.charts) {
            const root = document.documentElement
            root.style.setProperty(
                '--theme-charts-fill',
                clientTheme.charts.fill
            )
            root.style.setProperty(
                '--theme-charts-stroke',
                clientTheme.charts.stroke
            )
            root.style.setProperty(
                '--theme-charts-text',
                clientTheme.charts.text
            )
            root.style.setProperty(
                '--theme-charts-background',
                clientTheme.charts.background
            )
            root.style.setProperty(
                '--theme-charts-grid',
                clientTheme.charts.grid
            )
            root.style.setProperty(
                '--theme-charts-axis',
                clientTheme.charts.axis
            )
        }
    }, [clientTheme, themeVariantId])

    return {
        clientTheme,
        muiTheme,
        logoPath,
        logoIconPath,
        childLogoPath,
        themeVariantObj,
    }
}
